import Phaser from 'phaser';
import { System } from '../core/System';
import type { Entity } from '../core/Entity';
import { Transform } from '../components/Transform';
import { Sprite } from '../components/Sprite';

/**
 * Syncs ECS entities to Phaser display objects.
 */
export class RenderSystem extends System {
  requiredComponents = [Transform, Sprite];

  private _scene: Phaser.Scene;
  private _sprites = new Map<number, Phaser.GameObjects.Sprite>();

  constructor(scene: Phaser.Scene) {
    super();
    this._scene = scene;
  }

  update(entities: Entity[], _delta: number): void {
    const activeEntityIds = new Set<number>();

    for (const entity of entities) {
      activeEntityIds.add(entity.id);

      const transform = entity.get(Transform);
      const sprite = entity.get(Sprite);

      let phaserSprite = this._sprites.get(entity.id);

      // Create sprite if it doesn't exist
      if (!phaserSprite && sprite.texture) {
        phaserSprite = this._scene.add.sprite(transform.x, transform.y, sprite.texture);
        this._sprites.set(entity.id, phaserSprite);
      }

      // Update sprite properties
      if (phaserSprite) {
        phaserSprite.setPosition(transform.x, transform.y);
        phaserSprite.setRotation(transform.rotation);
        phaserSprite.setScale(transform.scaleX, transform.scaleY);
        phaserSprite.setOrigin(sprite.originX, sprite.originY);
        phaserSprite.setVisible(sprite.visible);
        phaserSprite.setAlpha(sprite.alpha);
        phaserSprite.setDepth(sprite.depth);

        if (sprite.tint !== undefined) {
          phaserSprite.setTint(sprite.tint);
        }

        if (sprite.width !== undefined && sprite.height !== undefined) {
          phaserSprite.setDisplaySize(sprite.width, sprite.height);
        }

        if (sprite.frame !== undefined) {
          phaserSprite.setFrame(sprite.frame);
        }
      }
    }

    // Remove sprites for destroyed entities
    for (const [entityId, phaserSprite] of this._sprites) {
      if (!activeEntityIds.has(entityId)) {
        phaserSprite.destroy();
        this._sprites.delete(entityId);
      }
    }
  }

  destroy(): void {
    for (const sprite of this._sprites.values()) {
      sprite.destroy();
    }
    this._sprites.clear();
  }
}
